import { ChevronDown, ChevronRight, FileText } from 'lucide-react';
import { useState } from 'react';

import { blockText } from '../../shared/export';
import type { LocalRecord, Note } from '../../shared/model';

import { notePath, RouteLink } from '../lib/routes';
import { IconButton, NoteIcon } from './ui';

export function NoteList({
  records,
  notes,
  selectedId,
  searching,
}: {
  records: LocalRecord[];
  notes: LocalRecord[];
  selectedId?: string;
  searching: boolean;
}) {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const byId = new Map(notes.map(note => [note.id, note]));

  return (
    <div className="note-tree">
      {records.map(record => {
        const note = record.value as Note,
          ancestors: string[] = [],
          seen = new Set([record.id]);

        let parent = note.parentId;

        while (parent && byId.has(parent) && !seen.has(parent)) {
          seen.add(parent);
          ancestors.push(parent);
          parent = (byId.get(parent)!.value as Note).parentId;
        }

        if (!searching && ancestors.some(id => collapsed.has(id))) {
          return null;
        }

        const children = notes.some(child => (child.value as Note).parentId === record.id);

        const expanded = searching || !collapsed.has(record.id),
          title = note.title || 'Untitled';

        return (
          <div
            key={record.id}
            className={`note-tree-row ${selectedId === record.id ? 'selected' : ''}`}
            style={{ paddingLeft: `${Math.min(ancestors.length, 8) * 12}px` }}
          >
            <RouteLink
              href={notePath(record.id)}
              className="note-list-item"
              aria-current={selectedId === record.id ? 'page' : undefined}
            >
              {note.icon ? (
                <NoteIcon className="note-list-icon" icon={note.icon} tone={note.iconTone} />
              ) : (
                <span className="note-list-icon" aria-hidden="true">
                  <FileText size={15} />
                </span>
              )}
              <span>
                <strong>{title}</strong>
                <small>{blockText(note.content).slice(0, 60) || 'Empty page'}</small>
              </span>
            </RouteLink>
            {children && (
              <IconButton
                size="compact"
                className="note-tree-toggle"
                label={`${expanded ? 'Collapse' : 'Expand'} children of ${title}`}
                aria-expanded={expanded}
                disabled={searching}
                title={
                  searching
                    ? 'Matching notes stay visible during search'
                    : `${expanded ? 'Collapse' : 'Expand'} nested notes`
                }
                onClick={() =>
                  setCollapsed(previous => {
                    const next = new Set(previous);

                    if (next.has(record.id)) {
                      next.delete(record.id);
                    } else {
                      next.add(record.id);
                    }

                    return next;
                  })
                }
              >
                {expanded ? (
                  <ChevronDown aria-hidden="true" />
                ) : (
                  <ChevronRight aria-hidden="true" />
                )}
              </IconButton>
            )}
          </div>
        );
      })}
    </div>
  );
}
